import { atom } from "jotai"
import { store } from "./store"
import { Sentry } from "@/telemetry/sentry"
import { resetDialogs } from "./dialogs-state"
import { queryClient } from "../api/constants"
import { isWindowsEnvironment } from "../utils/system"
import { showingSplashAtom } from "./ui-views-state"
import { getUserId, setUserId } from "@amplitude/analytics-browser"
import {
	electron,
	getShellVersion,
	getTemporaryUserId,
	isElectronApp,
	notifyAppReady,
	onCheckoutComplete,
	onEnterFullScreen,
	onLeaveFullScreen,
	onSystemResume,
	onSystemSuspend,
} from "@/utils/electron"
import { APP_SYSTEM_RESUME_EVENT, APP_SYSTEM_SUSPEND_EVENT } from "@/utils/system-sleep-events"

export const electronWindowMaximizedAtom = atom<boolean>(false)
let didBootstrapElectron = false

export const shouldApplyWindowToolbarCompensationAtom = atom((get) => {
	return isElectronApp && !isWindowsEnvironment() && !get(electronWindowMaximizedAtom)
})

export const bootstrapElectron = () => {
	if (!electron) return
	if (didBootstrapElectron) return
	didBootstrapElectron = true

	try {
		Sentry.setTag("electron", true)
		Sentry.setTag("electron_process", "renderer")
		Sentry.setTag("electron_shell_version", getShellVersion())

		onEnterFullScreen(() => {
			store.set(electronWindowMaximizedAtom, true)
		})

		onLeaveFullScreen(() => {
			store.set(electronWindowMaximizedAtom, false)
		})

		onCheckoutComplete(() => {
			resetDialogs()
			queryClient.refetchQueries({ queryKey: ["user"] }).catch(Sentry.captureException)
		})

		onSystemSuspend(() => {
			console.log("[SleepResume][Renderer] Received SYSTEM_SUSPEND from Electron")
			window.dispatchEvent(new Event(APP_SYSTEM_SUSPEND_EVENT))
		})

		onSystemResume(() => {
			console.log("[SleepResume][Renderer] Received SYSTEM_RESUME from Electron")
			window.dispatchEvent(new Event(APP_SYSTEM_RESUME_EVENT))
		})

		/**
		 * Electron bundles its own splash screen, notify when the app is ready to remove it
		 */
		store.sub(showingSplashAtom, () => {
			if (!store.get(showingSplashAtom)) {
				notifyAppReady(APP_VERSION)
			}
		})

		/**
		 * If we're not logged in, set the temporary user ID from the Electron wrapper to merge the profiles once authed
		 */
		if (!getUserId()) {
			const tempIdPromise = getTemporaryUserId()
			if (tempIdPromise) {
				tempIdPromise
					.then((userId) => {
						setUserId(userId)
					})
					.catch(Sentry.captureException)
			}
		}
	} catch (error) {
		Sentry.captureException(error)
	}
}
